import { NavLink, useLocation } from "react-router-dom";
import { BarChart3, CheckSquare, Bot, Settings, ChevronLeft, Building2, Folder, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

const navigation = [{
  name: "Dashboard",
  href: "/dashboard",
  icon: BarChart3
}, {
  name: "Obras",
  href: "/obras",
  icon: Building2
}, {
  name: "Gestor de Tarefas",
  href: "/tarefas",
  icon: CheckSquare
}, {
  name: "Relatórios",
  href: "/relatorios",
  icon: BarChart3
}, {
  name: "Obra IA",
  href: "/obra-ia",
  icon: Bot
}, {
  name: "Cadastro",
  href: "/cadastro",
  icon: Folder
}, {
  name: "Configurações",
  href: "/configuracoes",
  icon: Settings
}];

interface SidebarProps {
  /** Estado do drawer no mobile (controlado pelo AppLayout). */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  // `collapsed` vale só no DESKTOP (rail de ícones vs. expandido). No mobile a
  // sidebar vira um drawer que desliza por cima e sempre aparece expandida.
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  const isActive = (href: string) => {
    // Painel da obra (dashboard?obra=) pertence a Obras, não ao Dashboard
    if (href === "/obras" && location.pathname === "/dashboard" && location.search.includes("obra=")) {
      return true;
    }
    if (href === "/dashboard" && location.pathname === "/dashboard" && location.search.includes("obra=")) {
      return false;
    }
    return location.pathname === href;
  };

  return (
    <>
      {/* Backdrop — só no mobile, quando o drawer está aberto */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[55] bg-black/50 md:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <div
        style={{ left: mobileOpen ? 0 : "-16rem" }}
        className={cn(
          "bg-card border-r border-border flex flex-col shrink-0",
          // Mobile: drawer fixo off-canvas (posição por `left` inline; abertura
          // instantânea — a transição de posição não anima de forma confiável
          // neste setup). No desktop md:static torna o `left` inerte.
          // z acima dos cabeçalhos sticky das páginas (que usam z-50) para o
          // drawer não deixar o conteúdo vazar por cima quando aberto no mobile.
          "fixed inset-y-0 z-[60] w-64",
          "md:static md:z-auto",
          collapsed ? "md:w-16" : "md:w-64"
        )}
      >
        {/* Botão de recolher — apenas no desktop */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
          aria-expanded={!collapsed}
          className="hidden md:flex fixed top-1/2 -translate-y-1/2 z-50 w-6 h-6 bg-background border border-border rounded-full items-center justify-center hover:bg-accent transition-all shadow-lg"
          style={{ left: collapsed ? "52px" : "245px" }}
        >
          <ChevronLeft className={cn("w-4 h-4 text-muted-foreground transition-transform", collapsed && "rotate-180")} />
        </button>

        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between gap-2">
          <div className={cn("flex items-center gap-2", collapsed && "md:justify-center md:w-full")}>
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center shrink-0">
              <BarChart3 className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className={cn("font-semibold text-foreground", collapsed && "md:hidden")}>ObraIA</span>
          </div>
          {/* Fechar — só no mobile */}
          <button
            type="button"
            onClick={onMobileClose}
            aria-label="Fechar menu"
            className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 sm:p-4 space-y-1 overflow-y-auto">
          {navigation.map(item => {
            const isCurrentPage = isActive(item.href);
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={onMobileClose}
                title={collapsed ? item.name : undefined}
                aria-label={collapsed ? item.name : undefined}
                className={cn(
                  "flex items-center rounded-lg px-3 py-3 text-sm font-medium transition-colors",
                  collapsed && "md:justify-center md:px-2",
                  isCurrentPage
                    ? "bg-primary text-primary-foreground shadow-card"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}
              >
                <item.icon className={cn("w-5 h-5 shrink-0 mr-3", collapsed && "md:mr-0")} />
                <span className={cn(collapsed && "md:hidden")}>{item.name}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    </>
  );
}
